import { track } from '@vercel/analytics';

export interface ErrorContext {
  userId?: string;
  screen?: string;
  action?: string;
  userAgent?: string;
  url?: string;
  timestamp?: number;
  sessionId?: string;
  gameState?: any;
}

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 'bytes' | 'count';
  context?: Record<string, any>;
}

class ErrorTrackingService {
  private sessionId: string;
  private userId?: string;
  private context: ErrorContext = {};

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeContext();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeContext(): void {
    this.context = {
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: Date.now()
    };
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.context.userId = userId;
  }

  setScreen(screen: string): void {
    this.context.screen = screen;
  }

  // Log errors with context
  logError(error: Error | string, context?: Partial<ErrorContext>): void {
    const errorData = {
      message: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      ...this.context,
      ...context,
      timestamp: Date.now()
    };

    // Log to console for development
    console.error('[ErrorTracking]', errorData);

    // Track with Vercel Analytics (ensure all values are primitives)
    track('error', {
      message: typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message),
      screen: errorData.screen || 'unknown',
      action: errorData.action || 'unknown',
      user_id: errorData.userId || 'anonymous',
      session_id: this.sessionId
    });

    // Send to Supabase for persistent storage
    this.sendToSupabase('error', errorData).catch(console.warn);
  }

  // Log user actions for debugging
  logAction(action: string, data?: Record<string, any>): void {
    const actionData = {
      action,
      data,
      ...this.context,
      timestamp: Date.now()
    };

    console.log('[ActionTracking]', actionData);

    // Track user actions
    track('user_action', {
      action,
      screen: this.context.screen || 'unknown',
      user_id: this.userId || 'anonymous',
      session_id: this.sessionId
    });
  }

  // Log performance metrics
  logPerformance(metric: PerformanceMetric): void {
    const performanceData = {
      ...metric,
      ...this.context,
      timestamp: Date.now()
    };

    console.log('[Performance]', performanceData);

    track('performance', {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_unit: metric.unit,
      screen: this.context.screen || 'unknown',
      session_id: this.sessionId
    });
  }

  // Log API calls for debugging
  logApiCall(endpoint: string, method: string, duration: number, success: boolean, error?: string): void {
    const apiData = {
      endpoint,
      method,
      duration,
      success,
      error,
      ...this.context,
      timestamp: Date.now()
    };

    console.log('[API]', apiData);

    track('api_call', {
      endpoint,
      method,
      duration,
      success: success.toString(),
      error: error || 'none',
      session_id: this.sessionId
    });

    this.sendToSupabase('api_call', apiData).catch(console.warn);
  }

  // Log game events
  logGameEvent(event: string, data?: Record<string, any>): void {
    const gameData = {
      event,
      data,
      ...this.context,
      timestamp: Date.now()
    };

    console.log('[GameEvent]', gameData);

    track('game_event', {
      event,
      screen: this.context.screen || 'unknown',
      user_id: this.userId || 'anonymous',
      session_id: this.sessionId
    });
  }

  // Send data to Supabase for persistent logging
  private async sendToSupabase(type: string, data: any): Promise<void> {
    try {
      // TODO: Re-implement with databaseService
      // Previously used supabaseService.logEvent which needs to be migrated
      console.log('[ErrorTracking] Supabase logging temporarily disabled during refactoring');
    } catch (error) {
      console.warn('[ErrorTracking] Failed to send to Supabase:', error);
    }
  }

  // Get current context for debugging
  getContext(): ErrorContext {
    return { ...this.context };
  }

  // Measure performance of async operations
  async measureAsync<T>(name: string, operation: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await operation();
      const duration = performance.now() - startTime;
      
      this.logPerformance({
        name,
        value: Math.round(duration),
        unit: 'ms',
        context: { success: true }
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      this.logPerformance({
        name,
        value: Math.round(duration),
        unit: 'ms',
        context: { success: false, error: error instanceof Error ? error.message : 'unknown' }
      });
      
      throw error;
    }
  }

  // Measure performance of sync operations
  measure<T>(name: string, operation: () => T): T {
    const startTime = performance.now();
    try {
      const result = operation();
      const duration = performance.now() - startTime;
      
      this.logPerformance({
        name,
        value: Math.round(duration),
        unit: 'ms',
        context: { success: true }
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      this.logPerformance({
        name,
        value: Math.round(duration),
        unit: 'ms',
        context: { success: false, error: error instanceof Error ? error.message : 'unknown' }
      });
      
      throw error;
    }
  }
}

// Export singleton instance
export const errorTrackingService = new ErrorTrackingService();

// Export a React hook for easy usage
export const useErrorTracking = () => {
  return {
    logError: errorTrackingService.logError.bind(errorTrackingService),
    logAction: errorTrackingService.logAction.bind(errorTrackingService),
    logPerformance: errorTrackingService.logPerformance.bind(errorTrackingService),
    logGameEvent: errorTrackingService.logGameEvent.bind(errorTrackingService),
    setScreen: errorTrackingService.setScreen.bind(errorTrackingService),
    setUserId: errorTrackingService.setUserId.bind(errorTrackingService),
    measureAsync: errorTrackingService.measureAsync.bind(errorTrackingService),
    measure: errorTrackingService.measure.bind(errorTrackingService)
  };
}; 